import { Button } from 'components/atoms';
import InputMessage from 'components/atoms/inputs/wrapper/message/InputMessage';
import { FormikContext } from 'formik';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiCaretLeft, PiCaretRight, PiLock, PiWarning } from 'react-icons/pi';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTokenData } from 'store/state';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { II18nextPrefix } from 'types/components/components.interface';
import { hasTabWarning } from 'utils/global/validation.utils';
import {
  ITabsNavigationItem,
  useTabsNavigationContext,
} from './TabsNavigationContext';
import './TabsNavigation.scss';

export type { ITabsNavigationItem };

interface ITabsNavigationProps extends II18nextPrefix {
  tabs: ITabsNavigationItem[];
  disabled?: number[];
  displayMode?: boolean;
}

const TabsNavigation = ({
  i18nextPrefix,
  disabled,
  tabs,
  displayMode,
}: ITabsNavigationProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const location = useLocation();
  const tokenData = useTokenData();
  const tabsNavigationContext = useTabsNavigationContext();
  const formik = useContext(FormikContext);
  const isDisplayMode = !!(
    displayMode ??
    formik?.status?.displayMode ??
    tabsNavigationContext?.displayMode
  );
  const shouldShowValidationErrors =
    isDisplayMode || (formik?.submitCount ?? 0) > 0;

  const isTabActive = (name: string): boolean =>
    pathname.split('/').includes(name);

  const isTabDisabled = (value: number): boolean => !!disabled?.includes(value);

  const hasPermission = (
    permissionName?: string,
    permissionNames?: string[],
  ): boolean => {
    if (permissionNames?.length) {
      return permissionNames.some(
        name => !!tokenData?.[name as keyof typeof tokenData],
      );
    }
    if (!permissionName) return true;
    return !!tokenData?.[permissionName as keyof typeof tokenData];
  };

  const elementRef = useRef<HTMLDivElement | null>(null);

  const [isOverflowing, setIsOverflowing] = useState(false);

  const checkOverflow = () => {
    if (elementRef.current) {
      const isHorOverflowing =
        elementRef.current.scrollWidth > elementRef.current.clientWidth;
      setIsOverflowing(isHorOverflowing);
    }
  };

  useEffect(() => {
    window.addEventListener('resize', checkOverflow);
    return () => {
      window.removeEventListener('resize', checkOverflow);
    };
  }, []);

  const visibleTabs = useMemo(
    () =>
      tabs.filter(tab =>
        hasPermission(tab.permissionName, tab.permissionNames),
      ),
    [tabs, tokenData],
  );

  useEffect(() => {
    tabsNavigationContext?.setTabs(visibleTabs);
  }, [tabsNavigationContext, visibleTabs]);

  useEffect(() => {
    tabsNavigationContext?.setDisplayMode(isDisplayMode);
  }, [isDisplayMode, tabsNavigationContext]);

  useEffect(() => {
    checkOverflow();

    const animationFrame = window.requestAnimationFrame(checkOverflow);
    return () => window.cancelAnimationFrame(animationFrame);
  }, [pathname, displayMode, disabled, visibleTabs.length]);

  const handleScroll = (direction: 'left' | 'right') => {
    if (elementRef.current) {
      const scrollAmount = elementRef.current.clientWidth * 0.8;
      if (direction === 'right') {
        elementRef.current.scrollLeft += scrollAmount;
      } else {
        elementRef.current.scrollLeft -= scrollAmount;
      }
    }
  };

  return (
    <div className='tab-container'>
      <Button
        onClick={() => handleScroll('left')}
        onlyIcon
        variant={BUTTON_VARIANT.white}
        buttonStyle={{ borderRadius: 2, borderBottomLeftRadius: 12 }}
        style={{
          display: isOverflowing ? 'flex' : 'none',
        }}>
        <Button.Description description={t('button.previous')} />
        <Button.Icon icon={<PiCaretLeft />} />
      </Button>

      <div
        ref={elementRef}
        className='tabs-navigation'
        style={{
          overflowX: isOverflowing ? 'auto' : undefined,
          overflowY: !isOverflowing ? undefined : 'hidden',
        }}>
        {visibleTabs.map((tab, idx) => {
          const hasVisibleError =
            shouldShowValidationErrors && !!tab.error;
          const hasVisibleWarning = hasTabWarning(tab.warning);

          return (
            <Button
              wrapperClassNames={
                hasVisibleError || hasVisibleWarning ? 'is-message' : undefined
              }
              className={
                hasVisibleError
                  ? 'error'
                  : hasVisibleWarning
                    ? 'warning'
                    : undefined
              }
              style={
                {
                  // background: idx === 0 && isOverflowing ? 'white' : 'none',
                }
              }
              disabled={isTabDisabled(tab.value)}
              key={tab.name}
              isActive={isTabActive(tab.name)}
              onClick={() =>
                navigate(tab.name, {
                  relative: 'path',
                  replace: true,
                  state: location.state,
                })
              }
              variant={BUTTON_VARIANT.white}>
              {!hasVisibleError && isTabDisabled(tab.value) && (
                <Button.Icon icon={<PiLock />} />
              )}
              {(hasVisibleError || hasVisibleWarning) && (
                <Button.Icon icon={<PiWarning />} />
              )}
              <Button.Text text={t(`${i18nextPrefix}.tabs.${tab.name}`)} />
              {((hasVisibleError &&
                typeof tab.error === 'string' &&
                tab.error.length) ||
                !!tab.helper?.length) && (
                <InputMessage
                  helperText={tab.helper}
                  message={
                    hasVisibleError && typeof tab.error === 'string'
                      ? tab.error
                      : undefined
                  }
                />
              )}
            </Button>
          );
        })}
        <div
          className='extender'
          style={{
            display: !isOverflowing ? 'block' : 'none',
          }}
        />
      </div>

      <Button
        onClick={() => handleScroll('right')}
        onlyIcon
        variant={BUTTON_VARIANT.white}
        buttonStyle={{ borderRadius: 2, borderBottomRightRadius: 12 }}
        style={{
          display: isOverflowing ? 'flex' : 'none',
        }}>
        <Button.Description description={t('button.next-item')} />
        <Button.Icon icon={<PiCaretRight />} />
      </Button>
    </div>
  );
};

export default TabsNavigation;
